import { useState } from 'react'
import { Box, Button, HStack, Heading, Progress, Text, VStack } from '@chakra-ui/react'
import type { GrantImpact } from '../../../types/grant'
import { formatVotes, raceLabel, reachPercent, votesLine } from '../../../lib/impact'
import { ImpactExplainerSheet } from './ImpactExplainerSheet'

interface ImpactCardProps {
  impact: GrantImpact | undefined
  isLoading: boolean
  isError: boolean
  isOffline: boolean
}

const conversations = (n: number) => `${formatVotes(n)} conversation${n === 1 ? '' : 's'}`

function Row({ who, count, perVote, whoKey }: { who: string; count: number | undefined; perVote: number | null; whoKey: 'you' | 'team' }) {
  return (
    <Box flex="1 1 9rem" borderWidth="1px" borderColor="border.muted" borderRadius="md" bg="bg.subtle" p={3}>
      <Text color="fg.muted" fontSize="sm">{who}</Text>
      <Text fontSize="lg" fontWeight="semibold">{count == null ? '—' : conversations(count)}</Text>
      {count != null && perVote != null && <Text color="fg.muted" fontSize="sm">{votesLine(count, perVote, whoKey)}</Text>}
    </Box>
  )
}

/** [Canvasser redesign Phase 8 / spec D11 as amended by P8-2] Reach (always), votes (when the organizer's
 * rate is on), and an optional past-race context line. The layers are independent: votes off hides only the
 * votes layer (PF-6). Online read; offline shows the last numbers, never spins. */
export function ImpactCard({ impact, isLoading, isError, isOffline }: ImpactCardProps) {
  const [explainerOpen, setExplainerOpen] = useState(false)
  const perVote = impact?.conversationsPerVote ?? null
  const pct = impact ? reachPercent(impact.peopleTalkedTo, impact.universe) : null
  const ctx = impact?.context ?? null
  const showFooter = !!impact && (perVote != null || ctx != null)

  if (!impact && isOffline) {
    return (
      <Box borderWidth="1px" borderColor="border" borderRadius="lg" bg="surface" p={4}>
        <Heading size="sm" mb={2}>Your impact</Heading>
        <Text color="fg.muted">Your impact shows up when you're back online.</Text>
      </Box>
    )
  }

  return (
    <Box borderWidth="1px" borderColor="border" borderRadius="lg" bg="surface" p={4}>
      <Heading size="sm" mb={3}>Your impact</Heading>
      {!impact && isError && !isLoading ? (
        <Text color="fg.muted">Couldn't load your impact right now.</Text>
      ) : (
        <VStack align="stretch" gap={3}>
          <HStack gap={2} flexWrap="wrap" align="stretch">
            <Row who="You" whoKey="you" count={impact?.yourConversations} perVote={perVote} />
            <Row who="Team" whoKey="team" count={impact?.teamConversations} perVote={perVote} />
          </HStack>
          {impact && pct != null && (
            <VStack align="stretch" gap={1}>
              <Text>
                {`${formatVotes(impact.peopleTalkedTo)} of ${formatVotes(impact.universe)} people talked to (${pct}%)`}
              </Text>
              <Progress.Root value={pct} size="sm" colorPalette="brand" borderRadius="full" aria-label="People talked to">
                <Progress.Track><Progress.Range /></Progress.Track>
              </Progress.Root>
            </VStack>
          )}
          {ctx && (
            <Text>
              {`In the precincts you're walking, the ${ctx.year} ${raceLabel(ctx.office)} was decided by ${formatVotes(ctx.marginVotes)} votes.`}
            </Text>
          )}
          {showFooter && (
            <HStack gap={2} color="fg.muted" fontSize="sm">
              <Text>Estimates</Text>
              <Button variant="plain" size="sm" minH="48px" px={1} color="accent" textDecoration="underline"
                onClick={() => setExplainerOpen(true)}>
                How we figure this
              </Button>
            </HStack>
          )}
          {impact && isOffline && <Text color="fg.muted" fontSize="sm">Updated when you're back online.</Text>}
        </VStack>
      )}
      <ImpactExplainerSheet open={explainerOpen} onClose={() => setExplainerOpen(false)} perVote={perVote} hasContext={!!ctx} />
    </Box>
  )
}
